<template>
  <div>
    <Spinner v-if="isSearching" />

    <div class="notification is-info" v-if="hasEmptySearchResult">No matching GPS found.</div>

    <div class="notification is-danger" v-if="searchError">{{ searchError }}</div>

    <GPSSearchResultList
      v-if="showGPSList"
      :gpsDevices="searchResult"
      :selectedIMEI="selectedIMEI"
      :selectedDeviceId="selectedDeviceId"
      @gps-selected="selectGPS($event)"
    />
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import GPSSearchResultList from './GPSSearchResultList'

import { RESET_USERS_CACHE } from '@/modules/users/usersCacheStore.js'

import {
  GPS_DEVICES_SEARCH_STORE_NAMESPACE,
  SEARCH_GPS_DEVICES,
  IS_SEARCHING,
  GET_SEARCH_RESULT,
  GET_SEARCH_ERROR,
  HAS_EMPTY_SEARCH_RESULT,
  INVALIDATE_SEARCH_RESULTS
} from './gpsDevicesSearchStore'

import { SET_WEHUNT_GPS, GET_IMEI } from './weHuntGPSStore'

import { SET_GPS, GET_DEVICE_ID } from './gpsDeviceStore'

export default {
  name: 'GPSDevicesSearch',
  components: {
    Spinner,
    GPSSearchResultList
  },

  props: {
    query: {
      type: String,
      default: ''
    },

    selectedWeHuntGPSStorePath: {
      type: String,
      required: true
    },

    selectedGPSStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {}
  },

  mounted() {
    if (this.$route.name === 'gpsDevicesSearch') {
      // Only reset search when a new search has started
      this.resetSelectedGPS()

      const invalidateSearchResultsAction = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${INVALIDATE_SEARCH_RESULTS}`
      this.$store.dispatch(invalidateSearchResultsAction)
    }

    this.performSearch()
  },

  methods: {
    performSearch() {
      const query = this.query
      const searchGPSDevicesAction = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${SEARCH_GPS_DEVICES}`
      this.$store.dispatch(searchGPSDevicesAction, { query })
    },

    selectGPS(gps) {
      if (gps.deviceId) {
        const setGPSDeviceAction = `${this.selectedGPSStorePath}/${SET_GPS}`
        this.$store.dispatch(setGPSDeviceAction, { gps })

        const gpsStorePath = this.selectedGPSStorePath
        const deviceId = gps.deviceId
        this.$router.push({
          name: 'gpsDetails',
          params: { gpsStorePath, deviceId },
          query: this.$route.query
        })
      } else {
        const setWeHuntGPSAction = `${this.selectedWeHuntGPSStorePath}/${SET_WEHUNT_GPS}`
        this.$store.dispatch(setWeHuntGPSAction, { gps })

        const weHuntGPSStorePath = this.selectedWeHuntGPSStorePath
        const imei = gps.imei
        this.$router.push({
          name: 'wehuntGPSDetails',
          params: { gpsStorePath: weHuntGPSStorePath, imei },
          query: this.$route.query
        })
      }
    },

    resetSelectedGPS() {
      // Reset selected imei
      const setWeHuntGPSAction = `${this.selectedWeHuntGPSStorePath}/${SET_WEHUNT_GPS}`
      this.$store.dispatch(setWeHuntGPSAction, { gps: null })

      // Reset selected GPS device
      const setGPSDeviceAction = `${this.selectedGPSStorePath}/${SET_GPS}`
      this.$store.dispatch(setGPSDeviceAction, { gps: null })

      // Reset users cache
      const resetUsersCacheAction = `${this.usersCacheStorePath}/${RESET_USERS_CACHE}`
      this.$store.dispatch(resetUsersCacheAction)
    }
  },

  computed: {
    showGPSList() {
      if (!this.searchResult) {
        return false
      }

      return this.searchResult.length > 0
    },

    isSearching() {
      const isSearching = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${IS_SEARCHING}`
      return this.$store.getters[isSearching]
    },

    searchResult() {
      const getSearchResult = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${GET_SEARCH_RESULT}`
      return this.$store.getters[getSearchResult]
    },

    searchError() {
      const getSearchError = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${GET_SEARCH_ERROR}`
      return this.$store.getters[getSearchError]
    },

    selectedIMEI() {
      const getIMEI = `${this.selectedWeHuntGPSStorePath}/${GET_IMEI}`
      return this.$store.getters[getIMEI]
    },

    selectedDeviceId() {
      const getDeviceId = `${this.selectedGPSStorePath}/${GET_DEVICE_ID}`
      return this.$store.getters[getDeviceId]
    },

    hasEmptySearchResult() {
      const hasEmptySearchResult = `${GPS_DEVICES_SEARCH_STORE_NAMESPACE}/${HAS_EMPTY_SEARCH_RESULT}`
      return this.$store.getters[hasEmptySearchResult]
    }
  },

  watch: {
    searchResult() {
      if (!this.searchResult) {
        return
      }

      if (this.searchResult.length === 1) {
        this.selectGPS(this.searchResult[0])
      }
    }
  }
}
</script>

<style></style>
